<template>
  <v-card class="d-flex flex-column asset-dialog">
    <v-toolbar color="toolbar" density="compact">
      <v-icon icon="mdi-map-marker-radius" class="ms-4 me-2"/>
      <v-toolbar-title>
        <div class="text-subtitle-1 font-weight-medium">{{ planFullName(plan) }}</div>
        <div class="text-caption opacity-80">{{ positionText }}</div>
      </v-toolbar-title>
      <v-btn icon="mdi-close" :aria-label="t('assetPosition.close')" @click="emit('close')"/>
    </v-toolbar>
    <v-card-text class="pa-2 pa-sm-4 overflow-auto">
      <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact">{{ errorMessage }}</v-alert>
      <v-skeleton-loader v-else-if="!imageUrl" type="image" height="400"/>
      <div v-else class="plan-container">
        <img :src="imageUrl" :alt="planFullName(plan)" class="plan-image" draggable="false"/>
        <div :class="['marker-point', markerVerticalClass]" :style="markerStyle" :aria-label="`${label} ${positionText}`">
          <span class="pulse-ring"/>
          <v-icon class="marker-pin" icon="mdi-map-marker" color="#CE0058" size="48"/>
          <v-chip v-if="label" :class="['marker-label', labelAlignmentClass]" color="#CE0058" size="small" label>
            {{ label }}
          </v-chip>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue'
import {getPlanImageUrl, useLotoAreaStore} from '@/api/loto'
import {LotoPlan, planFullName} from '@/types/Loto'
import {t} from '@/lib/utils'

const props = defineProps<{ planId: number; marker: { x: number; y: number }; label: string; positionText: string }>()
const emit = defineEmits<{ close: [] }>()
const plan = ref<LotoPlan | null>(null)
const imageUrl = ref<string | null>(null)
const errorMessage = ref('')
const markerStyle = computed(() => ({left: `${props.marker.x * 100}%`, top: `${props.marker.y * 100}%`}))
const labelAlignmentClass = computed(() => props.marker.x >= .5 ? 'marker-label--left' : 'marker-label--right')
const markerVerticalClass = computed(() => props.marker.y <= .15 ? 'marker-point--below' : 'marker-point--above')

onMounted(async () => {
  try {
    const store = useLotoAreaStore()
    await store.load()
    plan.value = store.planById(props.planId) ?? null
    if (!plan.value?.image_path) throw new Error(t('assetPosition.imageUnavailable'))
    imageUrl.value = await getPlanImageUrl(plan.value.image_path)
    if (!imageUrl.value) throw new Error(t('assetPosition.imageUnavailable'))
  } catch (error: any) {
    errorMessage.value = error?.message ?? t('assetPosition.imageUnavailable')
  }
})
</script>

<style scoped>
.asset-dialog { max-height: 100vh; }
.plan-container { position: relative; width: 100%; line-height: 0; overflow: hidden; }
.plan-image { width: 100%; height: auto; display: block; user-select: none; -webkit-user-drag: none; }
.marker-point { position: absolute; width: 0; height: 0; z-index: 2; }
.marker-pin { position: absolute; left: 0; top: 0; filter: drop-shadow(0 2px 3px rgba(0, 0, 0, .55)); }
.marker-point--above .marker-pin { transform: translate(-50%, -100%); }
.marker-point--below .marker-pin { transform: translate(-50%, 0) rotate(180deg); }
.marker-label {
  position: absolute;
  top: -46px;
  width: max-content !important;
  min-width: max-content;
  max-width: none !important;
  overflow: visible;
  white-space: nowrap;
  line-height: normal;
  text-overflow: clip;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .3);
}
.marker-label--right { left: 24px; right: auto; }
.marker-label--left { left: auto; right: 24px; }
.marker-point--below .marker-label { top: 12px; }
.marker-label :deep(.v-chip__content) {
  min-width: max-content;
  overflow: visible;
  white-space: nowrap;
  text-overflow: clip;
}
.pulse-ring { position: absolute; left: 0; top: 0; width: 18px; height: 18px; border: 3px solid #CE0058; border-radius: 50%; transform: translate(-50%, -50%); animation: marker-pulse 1.8s ease-out infinite; }
@keyframes marker-pulse { 0% { opacity: .9; transform: translate(-50%, -50%) scale(.45); } 80%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(2.3); } }
@media (prefers-reduced-motion: reduce) { .pulse-ring { animation: none; } }
</style>
